import plur from 'plur';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryChannel } from '../catalog';

/** The bulk verbs that share the snackbar choreography. */
export type BulkVerb = 'send' | 'retry' | 'redeliver' | 'delete';

/** Present-continuous + past-tense copy per verb, ported from Ember `verbMap`. */
const VERB_COPY: Record<BulkVerb, { doing: string; done: string }> = {
  send: { doing: 'Sending', done: 'sent' },
  retry: { doing: 'Retrying', done: 'retried' },
  redeliver: { doing: 'Sending again', done: 'sent again' },
  delete: { doing: 'Deleting', done: 'deleted' },
};

/**
 * The noun for a single delivery, ported from the Ember `delivery.label` getter:
 * drafts are "draft", failures are "failed <channel>", else the channel short
 * name. Feeds the snackbar/confirm copy.
 */
export function deliveryLabel(delivery: Delivery): string {
  const shortName = getDeliveryChannel(delivery.action_type.replace(/_action$/, ''))?.shortName ?? 'message';
  if ((delivery.drafted ?? 0) > 0) return 'draft';
  if ((delivery.failed ?? 0) > 0) return `failed ${shortName}`;
  return shortName;
}

/**
 * The shared noun for a set of rows: the common `deliveryLabel` when every
 * targeted row agrees, otherwise the generic "message". Ported from the Ember
 * `itemLabel` getter. `ids` empty means "all loaded rows".
 */
export function itemLabelFor(deliveries: readonly Delivery[], ids: readonly string[]): string {
  const rows = ids.length > 0 ? deliveries.filter((delivery) => ids.includes(delivery.id)) : deliveries;
  if (rows.length === 0) return 'message';
  const labels = new Set(rows.map(deliveryLabel));
  return labels.size === 1 ? [...labels][0] : 'message';
}

export interface BulkSnackbarCopy {
  /** Persistent in-flight message ("Sending your 3 drafts…"). */
  pending: string;
  /** Success message ("Your 3 drafts will be sent soon"). */
  success: string;
}

/**
 * The persistent + success snackbar copy for a bulk verb, ported verbatim from
 * the Ember `_complete` choreography. The label is pluralized by `count`
 * (no leading number — matching Ember's `pluralize`).
 */
export function bulkSnackbarCopy(verb: BulkVerb, itemLabel: string, count: number): BulkSnackbarCopy {
  const { doing, done } = VERB_COPY[verb];
  const label = plur(itemLabel, count);
  return {
    pending: `${doing} your ${label}…`,
    success: `Your ${label} will be ${done} soon`,
  };
}
